/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { FeProvisioningServiceError } from "@tivocorp/data-domain-lib";

import { Locale, LocaleKeys } from "../locale";
import { attachHttpContext } from "./attachHttpContext";

/**
 * Builds a FeProvisioningServiceError from a caught fe-prov error. A service
 * error code (error.response.data.code) is preferred; when a response exists
 * but has no payload the NO_PAYLOAD sentinel is used; otherwise the HTTP client
 * (axios) error code is used so that connection/timeout failures are mapped.
 *
 * @param error - The error thrown by an fe-prov call
 * @returns The mapped service error, carrying the HTTP context
 */
export function buildFeProvisioningServiceError(
  error: unknown
): FeProvisioningServiceError {
  const isErrorObject: boolean = error !== null && typeof error === "object";
  const response: unknown =
    isErrorObject && "response" in (error as object)
      ? (error as Record<string, unknown>).response
      : undefined;
  const hasResponse: boolean =
    response !== null && typeof response === "object";
  const responseData: unknown = hasResponse
    ? (response as Record<string, unknown>).data
    : undefined;
  const isDataObject: boolean =
    responseData !== null &&
    responseData !== undefined &&
    typeof responseData === "object";
  const serverCode: string | undefined =
    isDataObject && "code" in (responseData as object)
      ? String((responseData as Record<string, unknown>).code)
      : undefined;
  const noPayload: boolean =
    hasResponse &&
    (responseData === undefined ||
      responseData === null ||
      responseData === "");
  const clientCode: string =
    isErrorObject && "code" in (error as object)
      ? String((error as Record<string, unknown>).code)
      : "";
  const message: string =
    isDataObject && "message" in (responseData as object)
      ? String((responseData as Record<string, unknown>).message)
      : Locale.getString(LocaleKeys.FE_PROVISIONING_SERVICE_REQUEST_FAILED);
  let code: string;
  if (serverCode !== undefined) {
    code = serverCode;
  } else if (noPayload) {
    code = "NO_PAYLOAD";
  } else {
    code = clientCode;
  }
  const serviceError = new FeProvisioningServiceError(code, message);
  serviceError.setDescription(message);
  // Keeps the 401 visible to executeGuarded, which wraps this call and
  // replays it once with a fresh token — it reads `error.response.status`.
  return attachHttpContext(serviceError, error);
}
